import styled from '@emotion/styled'
import { FunctionComponent } from 'react'
import { Fonts } from '~/assets/style/fonts'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Skeleton } from '~/components/Skeleton'
import { WithClassName } from '~/types/utils'
import { SiteOnboardingStepCard } from './components/SiteOnboardingStepCard'

const ILLUSTRATION_ICON_SIZE_PX = 190

const SubTitle = styled.span`
  ${Fonts.H2};
  ${Fonts.colors.Jet};
  font-weight: 500;
  margin-bottom: 4px;
`

const Text = styled.div`
  ${Fonts.P1};
  ${Fonts.colors.Jet};
`

const GetStarted = styled.div`
  margin-bottom: 20px;
`

const Steps = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`

type SiteStepKey = 'adUnits' | 'contextKeys' | 'gamAndAUnitPath' | 'biddersAndModules' | 'defaultStack'
const STEPS_ORDER: SiteStepKey[] = ['adUnits', 'contextKeys', 'gamAndAUnitPath', 'biddersAndModules', 'defaultStack']
const STEPS_CONFIG: { [key in SiteStepKey]: { name: string; description: string; optional?: boolean; requirements?: SiteStepKey[] } } = {
  adUnits: {
    name: 'Ad units',
    description: 'Start building your inventory by creating at least one ad unit per device.',
  },
  contextKeys: {
    name: 'Context keys',
    description: 'Create context keys to build the ad unit path dynamically.',
    optional: true,
  },
  gamAndAUnitPath: {
    name: 'GAM and ad unit path',
    description: 'Set GAM and define the ad unit path architecture.',
  },
  biddersAndModules: {
    name: 'Bidders and modules',
    description: 'Set the header bidding bidders and modules to use on this site.',
    optional: true,
  },
  defaultStack: {
    name: 'Default stack',
    description: 'Set the default stack: select the ad units and the monetisation rules to apply.',
    requirements: ['adUnits', 'gamAndAUnitPath'],
  },
}

type PureSiteOverviewSiteConfig = {
  [key in SiteStepKey]: {
    done?: boolean
    onClick: () => void
  }
}
/**
 * The Activation tab is the onboarding checklist and nothing else.
 *
 * SPA used to sit above the steps here as well, which put One's page-detection setting on a tab that is
 * otherwise a list of things still to do — it is not a step, it never completes, and a reader working
 * down the checklist had to step over it. It lives on the Configuration tab, beside the media's other
 * settings, which is the one place a setting is looked for.
 */
type PureSiteOverviewProps = WithClassName & {
  adManagementEnabled: boolean
  steps: PureSiteOverviewSiteConfig
  isLoading: boolean
}
const UnstyledPureSiteOverview: FunctionComponent<PureSiteOverviewProps> = ({ className, adManagementEnabled, steps, isLoading }) => {
  return (
    <div className={className}>
      {isLoading ? (
        <Skeleton bigger />
      ) : (
        <>
          {!adManagementEnabled ? (
            <IllustrationMessage
              iconName="night_sky"
              iconSize={`${ILLUSTRATION_ICON_SIZE_PX}px`}
              title="This media is not on One yet."
              message="Edit the media from the media list to enable One on it."
            />
          ) : (
            <>
              <GetStarted>
                <SubTitle>Get started</SubTitle>
                <Text>Unlock the full potential of your site in a few steps.</Text>
              </GetStarted>
              <Steps>
                {STEPS_ORDER.map((key) => {
                  const { requirements, ...config } = STEPS_CONFIG[key]
                  const requirementNames = (requirements || []).map((r) => STEPS_CONFIG[r].name)
                  const step = steps[key] ?? {}
                  const locked = (requirements || []).some((r) => !steps[r].done)
                  return (
                    <SiteOnboardingStepCard
                      key={key}
                      requirements={requirementNames}
                      locked={locked}
                      {...config}
                      {...step}
                      trackingLabel={`adm-sites-activation-${normalizeAsTrackingLabel(config.name)}-navigate`}
                    />
                  )
                })}
              </Steps>
            </>
          )}
        </>
      )}
    </div>
  )
}
export const PureSiteOverview = styled(UnstyledPureSiteOverview)``
